import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Download, Mail, CalendarClock, Trash2, FileText, FileSpreadsheet } from "lucide-react";
import { apiGet, API } from "../lib/api";
import axios from "axios";

const MODULE_LABEL = {
  overview: "CXO Overview",
  control: "Control Assurance",
  identity: "Identity Health",
  soc: "SOC Operations",
  firewall: "Firewall Health",
  webgw: "Web Gateway Health",
  vuln: "Vulnerability Management",
  email: "Email Security",
};

export default function ReportDialog({ module }) {
  const [open, setOpen] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [format, setFormat] = useState("pdf");
  const [schedule, setSchedule] = useState("once");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [status, setStatus] = useState(null);

  const refresh = () => apiGet("/report/schedules").then(setSchedules).catch(() => {});
  useEffect(() => { if (open) refresh(); }, [open]);

  const download = (fmt) => {
    const url = `${API}/report/${fmt}?module=${module}`;
    window.open(url, "_blank");
  };

  const send = async () => {
    setStatus(null);
    const list = recipients.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { setStatus({ ok: false, msg: "Please enter at least one recipient." }); return; }
    try {
      const r = await axios.post(`${API}/report/email`, {
        module, recipients: list, format, schedule,
        subject: subject || undefined, note: note || undefined,
      });
      setStatus({ ok: true, msg: `Queued ${schedule === "once" ? "for delivery" : `on ${schedule} schedule`} to ${list.length} recipient(s). ${r.data.delivery_note}` });
      refresh();
    } catch (e) {
      setStatus({ ok: false, msg: e?.response?.data?.detail || "Failed to queue report." });
    }
  };

  const removeSchedule = async (id) => {
    await axios.delete(`${API}/report/schedules/${id}`);
    refresh();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="report-btn" className="small-caps">
          <Download className="w-4 h-4 mr-1" /> Report
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl" style={{ color: "var(--cv-crimson)" }}>
            Reports · {MODULE_LABEL[module] || module}
          </DialogTitle>
          <DialogDescription>Download instantly, send to email, or schedule a recurring delivery.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="download" className="mt-2">
          <TabsList data-testid="report-tabs">
            <TabsTrigger value="download" data-testid="tab-download">Download</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">Send email</TabsTrigger>
            <TabsTrigger value="schedules" data-testid="tab-schedules">Schedules</TabsTrigger>
          </TabsList>

          <TabsContent value="download" className="pt-3">
            <div className="grid grid-cols-2 gap-3">
              <Button data-testid="dl-pdf" onClick={() => download("pdf")} style={{ background: "var(--cv-crimson)" }}>
                <FileText className="w-4 h-4 mr-2" /> Download PDF
              </Button>
              <Button data-testid="dl-csv" variant="outline" onClick={() => download("csv")}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Download CSV
              </Button>
            </div>
            <p className="text-[12.5px] mt-3" style={{ color: "var(--cv-charcoal-soft)" }}>
              PDF is board-ready · CSV is analyst-ready · both include current filter state and are watermarked "Board Confidential".
            </p>
          </TabsContent>

          <TabsContent value="email" className="pt-3 space-y-3">
            <div>
              <label className="small-caps text-[11.5px] font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>Recipients</label>
              <Input data-testid="email-recipients" placeholder="cfo@acme.com, ciso@acme.com" value={recipients} onChange={(e) => setRecipients(e.target.value)} />
              <p className="text-[11px] mt-1" style={{ color: "var(--cv-charcoal-soft)" }}>Comma-, semicolon-, or space-separated.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="small-caps text-[11.5px] font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>Format</label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger data-testid="email-format"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF (board)</SelectItem>
                    <SelectItem value="csv">CSV (data)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="small-caps text-[11.5px] font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>Cadence</label>
                <Select value={schedule} onValueChange={setSchedule}>
                  <SelectTrigger data-testid="email-schedule"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">One-off · now</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="small-caps text-[11.5px] font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>Subject (optional)</label>
              <Input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`CyberVisible · ${MODULE_LABEL[module]}`} />
            </div>
            <div>
              <label className="small-caps text-[11.5px] font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>Note (optional)</label>
              <Textarea data-testid="email-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Please review before the audit-committee call." />
            </div>
            <div className="flex items-center justify-between">
              <div className="text-[11.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>
                <strong style={{ color: "#9a6512" }}>NOTE:</strong> Delivery is <strong>MOCKED</strong> — records are stored in the report queue. Wire an SMTP integration (Resend / SendGrid) to enable real delivery.
              </div>
              <Button data-testid="email-send" onClick={send} style={{ background: "var(--cv-crimson)" }}>
                <Mail className="w-4 h-4 mr-2" /> {schedule === "once" ? "Send now" : `Save ${schedule}`}
              </Button>
            </div>
            {status && (
              <div data-testid="email-status" className="text-[12.5px] px-3 py-2 rounded"
                style={{ background: status.ok ? "rgba(62,124,79,0.13)" : "rgba(165,48,15,0.13)", color: status.ok ? "var(--cv-green)" : "var(--cv-crimson)" }}>
                {status.msg}
              </div>
            )}
          </TabsContent>

          <TabsContent value="schedules" className="pt-3">
            <div data-testid="schedules-list" className="max-h-[320px] overflow-y-auto">
              {schedules.length === 0 && <div className="text-sm opacity-60 py-4">No schedules yet.</div>}
              {schedules.map((s) => (
                <div key={s.id} data-testid={`schedule-${s.id}`} className="flex items-start justify-between py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                  <div>
                    <div className="text-[13.5px] font-bold">
                      {MODULE_LABEL[s.module] || s.module}
                      <span className="ml-2 status-pill status-at-risk">{s.schedule}</span>
                      <span className="ml-1 status-pill status-compliant">{s.format.toUpperCase()}</span>
                    </div>
                    <div className="text-[12px]" style={{ color: "var(--cv-charcoal-soft)" }}>
                      To: {s.recipients.join(", ")} · Created {new Date(s.created_at).toLocaleString()}
                    </div>
                    <div className="text-[11px] opacity-70">{s.status}</div>
                  </div>
                  <Button variant="ghost" size="sm" data-testid={`schedule-del-${s.id}`} onClick={() => removeSchedule(s.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
